import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getGame } from '../api/gameApi';

const inferSocketUrl = () => {
  if (typeof window === 'undefined') return 'http://localhost:5030';
  const host = window.location.hostname.toLowerCase();
  if (host === 'frolickingelfstudio.ca' || host.endsWith('.frolickingelfstudio.ca')) {
    return 'https://api-necro.frolickingelfstudio.ca';
  }
  return process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5030';
};

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL || inferSocketUrl();

/**
 * Connects to the /game namespace with JWT auth.
 * Returns { socket, gameState, error, swapCards, markReady, playCard, pickupPile, openingPass, sendGameChat, sendChat, setGameName, playAgainReady, playResult, gameOver }.
 */
const useSocket = (gameId) => {
  const socketRef = useRef(null);
  const [gameState, setGameState] = useState(null);
  const [error, setError] = useState(null);
  const [playResult, setPlayResult] = useState(null);
  const [gameOver, setGameOver] = useState(null);
  const [inactivityBootEvent, setInactivityBootEvent] = useState(null);
  const [inactivityCheckPrompt, setInactivityCheckPrompt] = useState(null);
  const [waitlistJoinOffer, setWaitlistJoinOffer] = useState(null);
  const [voiceEvent, setVoiceEvent] = useState(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    if (!gameId) return;
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setError('Not authenticated — please log in again.');
      return;
    }

    // Reuse existing connected socket (guards against React StrictMode double-invoke)
    if (socketRef.current?.connected) return;

    const socket = io(`${SOCKET_URL}/game`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setError(null);
      socket.emit('join-room', { gameId });
    });

    socket.on('connect_error', (err) => {
      const detail = err?.message ? `${err.message}` : 'Unknown websocket connection failure.';
      setError(`Connection error while reaching ${SOCKET_URL}/game: ${detail}`);
    });

    socket.on('disconnect', (reason) => {
      if (reason === 'io client disconnect') return;
      setError(`Socket disconnected: ${reason}. Check backend at ${SOCKET_URL}.`);
    });

    socket.on('game-state', (state) => {
      setGameState(state);
      if (state?.status !== 'finished') setGameOver(null);
    });
    socket.on('room-update', (state) => {
      setGameState(state);
      if (state?.status !== 'finished') setGameOver(null);
    });
    socket.on('game-started', (data) => {
      setGameState((prev) =>
        prev
          ? { ...prev, status: 'playing', currentTurnIndex: data.currentTurnIndex, openingCardId: data.openingCardId }
          : prev
      );
    });
    socket.on('play-result', (result) => setPlayResult(result));
    socket.on('game-over', (data) => setGameOver(data));
    socket.on('tournament-inactivity-booted', (payload) => setInactivityBootEvent(payload || {}));
    socket.on('inactivity-check-prompt', (payload) => setInactivityCheckPrompt(payload || null));
    socket.on('waitlist-join-offer', (payload) => setWaitlistJoinOffer(payload || null));
    socket.on('voice-room-state', (payload) => setVoiceEvent({ type: 'voice-room-state', payload: payload || {} }));
    socket.on('voice-user-joined', (payload) => setVoiceEvent({ type: 'voice-user-joined', payload: payload || {} }));
    socket.on('voice-user-left', (payload) => setVoiceEvent({ type: 'voice-user-left', payload: payload || {} }));
    socket.on('voice-offer', (payload) => setVoiceEvent({ type: 'voice-offer', payload: payload || {} }));
    socket.on('voice-answer', (payload) => setVoiceEvent({ type: 'voice-answer', payload: payload || {} }));
    socket.on('voice-ice-candidate', (payload) => setVoiceEvent({ type: 'voice-ice-candidate', payload: payload || {} }));
    socket.on('voice-decline', (payload) => setVoiceEvent({ type: 'voice-decline', payload: payload || {} }));
    socket.on('error', ({ message }) => setError(message));

    // Fallback state sync for missed socket pushes.
    let mounted = true;
    const pollState = async () => {
      try {
        const { data } = await getGame(gameId);
        if (!mounted) return;
        setGameState(data);
      } catch {
        // Ignore polling failures; socket events remain primary.
      }
    };
    pollState();
    const pollTimer = setInterval(pollState, 4000);

    return () => {
      mounted = false;
      clearInterval(pollTimer);
      socket.off('connect');
      socket.off('connect_error');
      socket.off('disconnect');
      socket.off('game-state');
      socket.off('room-update');
      socket.off('game-started');
      socket.off('play-result');
      socket.off('game-over');
      socket.off('tournament-inactivity-booted');
      socket.off('inactivity-check-prompt');
      socket.off('waitlist-join-offer');
      socket.off('voice-room-state');
      socket.off('voice-user-joined');
      socket.off('voice-user-left');
      socket.off('voice-offer');
      socket.off('voice-answer');
      socket.off('voice-ice-candidate');
      socket.off('voice-decline');
      socket.off('error');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [gameId]);

  const swapCards = useCallback((give, take) => {
    socketRef.current?.emit('swap-cards', { give, take });
  }, []);

  const markReady = useCallback(() => {
    socketRef.current?.emit('swap-ready');
    setTimeout(() => {
      socketRef.current?.emit('dealer-swap-check');
    }, 100);
  }, []);

  const playCard = useCallback((cardIds, source) => {
    // cardIds can be a single string or an array
    const ids = Array.isArray(cardIds) ? cardIds : [cardIds];
    socketRef.current?.emit('play-card', { cardIds: ids, source });
  }, []);

  const pickupPile = useCallback(() => {
    socketRef.current?.emit('pickup-pile');
  }, []);

  const openingPass = useCallback(() => {
    socketRef.current?.emit('opening-pass');
  }, []);

  const sendGameChat = useCallback((text, recipientUserId = '') => {
    socketRef.current?.emit('send-chat', { text, recipientUserId });
  }, []);

  const sendLoungeChat = useCallback((text, recipientUserId = '') => {
    socketRef.current?.emit('send-chat', { text, recipientUserId });
  }, []);

  // Backward-compatible alias
  const sendChat = sendGameChat;

  const setGameName = useCallback((name) => {
    socketRef.current?.emit('set-game-name', { name });
  }, []);

  const playAgainReady = useCallback(() => {
    socketRef.current?.emit('play-again-ready');
  }, []);

  const respondInactivityCheck = useCallback((promptId, stillPlaying = true) => new Promise((resolve, reject) => {
    if (!socketRef.current) {
      reject(new Error('Socket is not connected'));
      return;
    }
    socketRef.current.emit('inactivity-check-response', { promptId, stillPlaying: !!stillPlaying }, (response = {}) => {
      if (response.ok) {
        resolve(response);
        return;
      }
      reject(new Error(response.error || 'Could not process inactivity check response'));
    });
  }), []);

  const joinVoiceRoom = useCallback((target = 'game') => {
    socketRef.current?.emit('voice-join', { gameId, target });
  }, [gameId]);

  const leaveVoiceRoom = useCallback((target = 'game') => {
    socketRef.current?.emit('voice-leave', { gameId, target });
  }, [gameId]);

  const sendVoiceOffer = useCallback((toUserId, sdp, target = 'game') => {
    socketRef.current?.emit('voice-offer', { gameId, target, toUserId, sdp });
  }, [gameId]);

  const sendVoiceAnswer = useCallback((toUserId, sdp, target = 'game') => {
    socketRef.current?.emit('voice-answer', { gameId, target, toUserId, sdp });
  }, [gameId]);

  const sendVoiceIceCandidate = useCallback((toUserId, candidate, target = 'game') => {
    socketRef.current?.emit('voice-ice-candidate', {
      gameId,
      target,
      toUserId,
      candidate,
    });
  }, [gameId]);

  const sendVoiceDecline = useCallback((toUserId, reason = '', target = 'game') => {
    socketRef.current?.emit('voice-decline', {
      gameId,
      target,
      toUserId,
      reason,
    });
  }, [gameId]);

  return {
    socket: socketRef.current,
    gameState,
    error,
    clearError,
    swapCards,
    markReady,
    playCard,
    pickupPile,
    openingPass,
    sendGameChat,
    sendLoungeChat,
    sendChat,
    setGameName,
    playAgainReady,
    playResult,
    gameOver,
    inactivityBootEvent,
    inactivityCheckPrompt,
    waitlistJoinOffer,
    respondInactivityCheck,
    voiceEvent,
    joinVoiceRoom,
    leaveVoiceRoom,
    sendVoiceOffer,
    sendVoiceAnswer,
    sendVoiceIceCandidate,
    sendVoiceDecline,
  };
};

export default useSocket;
